import { collection, doc } from "firebase/firestore";
import {
  BarChart3,
  Check,
  ClipboardList,
  Megaphone,
  Pencil,
  Plus,
  Send,
  Trash2,
} from "lucide-react";
import { useEffect, useState } from "react";

import { db } from "integrations/firebase/firestore";

import { EmptyState, LoadingRows } from "components/states";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import { Card, CardContent } from "components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogPinnedFooter,
} from "components/ui/dialog";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Textarea } from "components/ui/textarea";

import { formatFormTime } from "../../feedback/format";
import { windowState } from "../../feedback/logic";
import type { FeedbackForm } from "../../feedback/schemas";
import { useAdminFeedbackForms } from "../../feedback/use-feedback";
import { AdminCommandFeedback } from "../AdminCommandFeedback";
import { useAdminCommand } from "../use-admin-command";

import { FeedbackEditor } from "./FeedbackEditor";
import { FeedbackResults } from "./FeedbackResults";

type View =
  | { kind: "list" }
  | { kind: "edit"; formId: string }
  | { kind: "results"; formId: string };

const MAX_ANNOUNCEMENT_TITLE = 120;
const MAX_ANNOUNCEMENT_BODY = 1000;

export function announcementDraft(form: Pick<FeedbackForm, "title">): {
  title: string;
  body: string;
} {
  return {
    title: `Feedback: ${form.title}`.slice(0, MAX_ANNOUNCEMENT_TITLE),
    body: `Please tell us what you thought of ${form.title}. It only takes a minute.`.slice(
      0,
      MAX_ANNOUNCEMENT_BODY,
    ),
  };
}

/**
 * Built-in feedback forms: the organiser writes the questions, attendees
 * answer in the app, and the results stay here rather than with a third-party
 * form host.
 */
export function FeedbackSection({
  eventId,
  uid,
  timeZone,
}: {
  eventId: string;
  uid: string;
  timeZone?: string;
}) {
  const forms = useAdminFeedbackForms(eventId);
  const [view, setView] = useState<View>({ kind: "list" });
  const toList = () => setView({ kind: "list" });

  if (forms.status === "loading") return <LoadingRows rows={3} />;
  if (forms.status === "error") {
    return (
      <EmptyState icon={ClipboardList} title="Could not load feedback forms">
        {forms.error}
      </EmptyState>
    );
  }

  const byId = (formId: string) =>
    forms.data.find((form) => form.id === formId) ?? null;

  if (view.kind === "edit") {
    return (
      <FeedbackEditor
        key={view.formId}
        eventId={eventId}
        uid={uid}
        formId={view.formId}
        form={byId(view.formId)}
        timeZone={timeZone}
        onBack={toList}
      />
    );
  }

  if (view.kind === "results") {
    const form = byId(view.formId);
    if (form) {
      return <FeedbackResults eventId={eventId} form={form} onBack={toList} />;
    }
  }

  return (
    <FormList
      eventId={eventId}
      uid={uid}
      timeZone={timeZone}
      forms={forms.data}
      onCreate={() =>
        setView({
          kind: "edit",
          formId: doc(collection(db, "events", eventId, "feedback_forms")).id,
        })
      }
      onEdit={(formId) => setView({ kind: "edit", formId })}
      onResults={(formId) => setView({ kind: "results", formId })}
    />
  );
}

function FormList({
  eventId,
  uid,
  timeZone,
  forms,
  onCreate,
  onEdit,
  onResults,
}: {
  eventId: string;
  uid: string;
  timeZone?: string;
  forms: FeedbackForm[];
  onCreate: () => void;
  onEdit: (formId: string) => void;
  onResults: (formId: string) => void;
}) {
  const command = useAdminCommand();
  const [deleting, setDeleting] = useState<FeedbackForm | null>(null);
  const [announcing, setAnnouncing] = useState<FeedbackForm | null>(null);
  const busy =
    command.state.status === "submitting" ||
    command.state.status === "watching";
  const sorted = [...forms].sort((a, b) => a.title.localeCompare(b.title));

  return (
    <div className="max-w-3xl min-w-0 space-y-4" data-testid="feedback-content">
      <div className="flex flex-wrap items-center gap-3">
        <p className="min-w-0 flex-1 text-sm text-muted-foreground">
          Ask attendees for feedback in the app. Open forms appear on the event
          page.
        </p>
        <Button
          variant="cta"
          className="h-11"
          onClick={onCreate}
          data-testid="feedback-new-form"
        >
          <Plus className="h-4 w-4" />
          New feedback form
        </Button>
      </div>

      <AdminCommandFeedback
        state={command.state}
        onReset={command.reset}
        testId="feedback-command"
      />

      {sorted.length === 0 ? (
        <EmptyState icon={ClipboardList} title="No feedback forms yet">
          Create one, open it, and attendees can answer from the event page.
        </EmptyState>
      ) : (
        sorted.map((form) => (
          <FormCard
            key={form.id}
            form={form}
            timeZone={timeZone}
            busy={busy}
            onEdit={() => onEdit(form.id)}
            onResults={() => onResults(form.id)}
            onAnnounce={() => setAnnouncing(form)}
            onDelete={() => setDeleting(form)}
          />
        ))
      )}

      <Dialog
        open={deleting !== null}
        onOpenChange={(open) => !open && setDeleting(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete this feedback form?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground wrap-anywhere">
            {deleting?.title} and all {deleting?.responseCount ?? 0} of its
            responses are deleted for good. Export the results first if you need
            them.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={busy}
              data-testid="confirm-delete-feedback-form"
              onClick={() => {
                if (!deleting) return;
                void command.submit("DELETE_FEEDBACK_FORM", eventId, uid, {
                  formId: deleting.id,
                });
                setDeleting(null);
              }}
            >
              <Trash2 className="h-4 w-4" />
              Delete form
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AnnounceDialog
        form={announcing}
        busy={busy}
        onClose={() => setAnnouncing(null)}
        onSend={(title, body) => {
          if (!announcing) return;
          void command.submit("SEND_EVENT_ANNOUNCEMENT", eventId, uid, {
            title,
            body,
            feedbackFormId: announcing.id,
          });
          setAnnouncing(null);
        }}
      />
    </div>
  );
}

function FormCard({
  form,
  timeZone,
  busy,
  onEdit,
  onResults,
  onAnnounce,
  onDelete,
}: {
  form: FeedbackForm;
  timeZone?: string;
  busy: boolean;
  onEdit: () => void;
  onResults: () => void;
  onAnnounce: () => void;
  onDelete: () => void;
}) {
  const state = windowState(form);
  const statusLabel = {
    accepting: "Open",
    closed: "Closed",
    "not-yet-open": "Scheduled",
    ended: "Ended",
  }[state];

  return (
    <Card data-testid={`feedback-form-${form.id}`}>
      <CardContent className="space-y-3 p-4">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <p className="min-w-0 flex-1 font-medium text-foreground wrap-anywhere">
            {form.title}
          </p>
          <Badge
            variant="outline"
            className={
              state === "accepting"
                ? "gap-1 border-status-success bg-status-success/15"
                : "gap-1"
            }
            data-testid="feedback-form-status"
          >
            {state === "accepting" && (
              <Check className="h-3.5 w-3.5" aria-hidden="true" />
            )}
            {statusLabel}
          </Badge>
          {form.anonymous && <Badge variant="outline">Anonymous</Badge>}
        </div>
        <p className="text-sm text-muted-foreground">
          {form.questions.length}{" "}
          {form.questions.length === 1 ? "question" : "questions"} ·{" "}
          <span data-testid="feedback-form-responses">
            {form.responseCount}{" "}
            {form.responseCount === 1 ? "response" : "responses"}
          </span>
          {form.opensAt && ` · opens ${formatFormTime(form.opensAt, timeZone)}`}
          {form.closesAt &&
            ` · closes ${formatFormTime(form.closesAt, timeZone)}`}
        </p>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            className="h-11"
            onClick={onEdit}
            data-testid="feedback-edit"
          >
            <Pencil className="h-4 w-4" />
            Edit
          </Button>
          <Button
            variant="outline"
            className="h-11"
            onClick={onResults}
            data-testid="feedback-open-results"
          >
            <BarChart3 className="h-4 w-4" />
            Results
          </Button>
          {/* Only while it takes answers: an announcement linking a form that
              is closed or outside its window is refused by the backend. */}
          {state === "accepting" && (
            <Button
              variant="outline"
              className="h-11"
              disabled={busy}
              onClick={onAnnounce}
              data-testid="feedback-announce"
            >
              <Megaphone className="h-4 w-4" />
              Announce
            </Button>
          )}
          <Button
            variant="destructive"
            className="h-11"
            disabled={busy}
            onClick={onDelete}
            data-testid="feedback-delete"
          >
            <Trash2 className="h-4 w-4" />
            Delete
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function AnnounceDialog({
  form,
  busy,
  onClose,
  onSend,
}: {
  form: FeedbackForm | null;
  busy: boolean;
  onClose: () => void;
  onSend: (title: string, body: string) => void;
}) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");

  useEffect(() => {
    if (!form) return;
    const draft = announcementDraft(form);
    setTitle(draft.title);
    setBody(draft.body);
  }, [form]);

  const valid = title.trim().length > 0 && body.trim().length > 0;

  return (
    <Dialog open={form !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Announce this form</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          Everyone registered for this event gets a notification with a button
          that opens the form in the app. Sending cannot be undone.
        </p>
        <div className="space-y-1.5">
          <Label htmlFor="feedback-announce-title">Title</Label>
          <Input
            id="feedback-announce-title"
            testId="feedback-announce-title"
            value={title}
            maxLength={MAX_ANNOUNCEMENT_TITLE}
            onChange={(event) => setTitle(event.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="feedback-announce-body">Message</Label>
          <Textarea
            id="feedback-announce-body"
            data-testid="feedback-announce-body"
            value={body}
            maxLength={MAX_ANNOUNCEMENT_BODY}
            rows={4}
            onChange={(event) => setBody(event.target.value)}
          />
        </div>
        <DialogPinnedFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="cta"
            disabled={busy || !valid}
            data-testid="confirm-feedback-announce"
            onClick={() => onSend(title.trim(), body.trim())}
          >
            <Send className="h-4 w-4" />
            Send to everyone
          </Button>
        </DialogPinnedFooter>
      </DialogContent>
    </Dialog>
  );
}
